import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  withStyles,
  Box,
  Button,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import { Check } from "@material-ui/icons";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import { useIsMounted } from "../../../../../hooks/useIsMounted";
import { useLocation } from "react-router-dom";
import MuiAlert from "@material-ui/lab/Alert";

const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
  // Custom styles for always-enabled statuses
  alwaysEnabled: {
    "& .MuiSwitch-track": {
      backgroundColor: "#8BC6FD !important",
    },
    "&$checked": {
      "& + $track": {
        backgroundColor: "#8BC6FD !important",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#8BC6FD !important",
      },
    },
  },
}))(({ classes, isAlwaysEnabled, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    classes={{
      root: classes.root,
      switchBase: `${classes.switchBase} ${isAlwaysEnabled ? classes.alwaysEnabled : ''}`,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

/**
 * StatusSelection Component
 *
 * Allows users to select and configure status options for a pharmaceutical product.
 * Manages PO statuses.
 * Features:
 *  - Toggle switches for activating/deactivating statuses
 *  - API integration to fetch and save statuses
 *  - Preserves user selections between wizard steps
 *  - Supports read-only mode
 */
const StatusSelection = ({
  formData, // Data from parent component containing all form steps
  updateStepData, // Function to update parent's form data
  classes, // CSS classes for styling
  readOnly = false, // Whether the form should be in read-only mode
  handleNext, // Function to move to the next step
  handleBack, // Function to go back to the previous step
  handleSubmit, // Function to submit the entire form (for final step)
  activeStep, // Current active step in the wizard
  stepsLength, // Total number of steps in the wizard
  isStatusEditMode = false, // Whether we're in edit mode for status selection
  cameFromBack = false,
  onValidationChange = () => { },
  markApiCallSuccessful, // Function to mark API call as successful and show success message
}) => {
  const isMounted = useIsMounted();
  // Step data for PO status
  const [stepData, setStepData] = useState({
    poStatuses: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialSaveSuccess, setIsInitialSaveSuccess] = useState(() => {
    const savedValue = sessionStorage.getItem('addedStatusSelection');
    return savedValue === "true";
  });
  // Persist emptyFgStatuses in sessionStorage
  const [emptyFgStatuses, setEmptyFgStatusesState] = useState(() => {
    const saved = sessionStorage.getItem('emptyFgStatuses');
    return saved === 'true';
  });
  // Helper to set and persist emptyFgStatuses
  const setEmptyFgStatuses = (val) => {
    setEmptyFgStatusesState(val);
    if (val) {
      sessionStorage.setItem('emptyFgStatuses', 'true');
    } else {
      sessionStorage.removeItem('emptyFgStatuses');
    }
  };
  // Ref to track previous formData for comparison
  const prevFormDataRef = useRef(null);
  // API base URL - replace with your actual API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");
  
    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, [
    localStorage.getItem("appName"),
    localStorage.getItem("network"),
    localStorage.getItem("collabType"),
    localStorage.getItem("roleName"),
  ]);
  const location = useLocation();
  const editPendingStatus = location.state?.isPending && location.state?.isEdit;
  const isNotPending = location.state?.isEdit && location.state?.isNotPending;
  const selectedProduct = location.state?.selectedProduct || {};
  const selectedProductFormData = location.state?.selectedProductFormData;
  const viewOnlyProductDetails = location.state?.viewProductDetails;
  const selectedCollabrixID = location.state?.productDetails?.collabrixProductID;
  const [updateModeData, setUpdateModeData] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
    key: Date.now(), // Add a key to force re-render
  });

  // Create Alert component for Snackbar
  const Alert = (props) => {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
  };

  // Read selectedReplicatedFromProductID from sessionStorage
  const [selectedReplicatedFromProductID, setSelectedReplicatedFromProductID] = useState(
    sessionStorage.getItem("selectedReplicatedFromProductID") || ""
  );

  useEffect(() => {
    setSelectedReplicatedFromProductID(sessionStorage.getItem("selectedReplicatedFromProductID") || "");
  }, []);

  // Effect for edit pending status
  useEffect(() => {
    const collabrixProductID = formData?.productDetails?.collabrixProductID;
    if ((editPendingStatus || isInitialSaveSuccess) && collabrixProductID) {
      const callData = async () => {
        try {
          setIsLoading(true); // Set loading to true before fetching
          const response = await axios.get(
            `${apiBaseUrl}/fetchProductDetails/${collabrixProductID}`
          );
          if (response.data && response.data.status) {
            const apiData = response.data.data;
            setUpdateModeData(apiData)
            const activatedStatuses = apiData.productFGStatuses?.fgStatusesJSON || {};

            if (Object.keys(activatedStatuses)?.length === 0) {
              setEmptyFgStatuses(true);
              // When empty, fetch and show all statuses with their activation state
              await fetchFGStatus();
              return;
            }

            // When we have data, first fetch all statuses if we're in edit mode
            await fetchFGStatus();

            // Then update with only the activated ones from the response
            setStepData(prevData => {
              const updatedData = { ...prevData };

              // Update PO Statuses
              if (Array.isArray(activatedStatuses.poStatuses)) {
                updatedData.poStatuses = prevData.poStatuses.map(status => {
                  const isActivated = activatedStatuses.poStatuses.some(
                    activated => activated.status_id === status.status_id && activated.activated === true
                  );
                  return {
                    ...status,
                    value: isActivated
                  };
                });
              }

              // Update parent component
              if (typeof updateStepData === "function") {
                updateStepData("fgStatuses", updatedData);
              }
              return updatedData;
            });
          } else {
            setError("Failed to fetch product details.");
          }
        } catch (error) {
          setError("An error occurred while fetching product details.");
        } finally {
          if (isMounted()) {
            setIsLoading(false); // Set loading to false after all operations
          }
        }
      };
      if(!readOnly) {
        callData();
      }
    } else {
      setIsLoading(false); // Set loading to false if we're not fetching
    }
  }, [editPendingStatus, isInitialSaveSuccess, formData?.productDetails?.collabrixProductID]);


  // Effect for status edit mode
  useEffect(() => {
    if (isStatusEditMode && !readOnly) {
      const updateStatusData = async () => {
        try {
          setIsLoading(true);
          // First fetch all available statuses
          await fetchFGStatus();


          // Then update with the activated ones from formData
          const activatedStatuses = stepData || {};
          
          setStepData(prevData => {
            const updatedData = { ...prevData };

            // Update PO Statuses
            if (Array.isArray(activatedStatuses.poStatuses)) {
              updatedData.poStatuses = prevData.poStatuses.map(status => {
                const isActivated = activatedStatuses.poStatuses.some(
                  activated => activated.status_id === status.status_id && activated.value === true
                );
                return {
                  ...status,
                  value: isActivated
                };
              });
            }

            // Update parent component
            if (typeof updateStepData === "function") {
              updateStepData("fgStatuses", updatedData);
            }
            return updatedData;
          });
        } catch (error) {
          setError("An error occurred while updating status data.");
        } finally {
          if (isMounted()) {
            setIsLoading(false);
          }
        }
      };

      updateStatusData();
    }
  }, [isStatusEditMode]);

  // Initialize data from formData if available
  useEffect(() => {
    if (isMounted() && formData?.fgStatuses) {
      const currentFormData = JSON.stringify(formData.fgStatuses);
      if (currentFormData !== JSON.stringify(prevFormDataRef.current)) {
        // Replica flow: show only activated statuses
        if (
          selectedReplicatedFromProductID &&
          location.state?.addNewProductConfig &&
          readOnly
        ) {
          const viewModeData = {
            poStatuses: (formData.fgStatuses.poStatuses || [])
              .filter(status => status.activated === true)
              .map(status => ({ ...status, value: true })),
          };
          setStepData(viewModeData);
          prevFormDataRef.current = viewModeData;
          setIsLoading(false);
        }
        // General readOnly/viewOnly: show all statuses, toggled ON for activated
        else if (viewOnlyProductDetails || readOnly) {
          const allStatusesWithActivated = {
            poStatuses: (formData.fgStatuses.poStatuses || []).map(status => {
              return { ...status, value: status.activated };
            }),
          };
          setStepData(allStatusesWithActivated);
          prevFormDataRef.current = allStatusesWithActivated;
          setIsLoading(false);
        }
        // Other cases
        else if (isNotPending && !editPendingStatus && !isStatusEditMode) {
          const viewModeData = {
            poStatuses: (formData.fgStatuses.poStatuses || []).filter(status => status.activated === true),
          };
          setStepData(viewModeData);
          prevFormDataRef.current = viewModeData;
          setIsLoading(false);
        } else {
          setStepData(formData.fgStatuses);
          prevFormDataRef.current = formData.fgStatuses;
          setIsLoading(false);
        }
      } else {
        setIsLoading(false); // fallback if data is unchanged
      }
    } else {
      setIsLoading(false); // fallback if no fgStatuses
    }
  }, [
    formData?.fgStatuses,
    isMounted(),
    isNotPending,
    editPendingStatus,
    isStatusEditMode,
    viewOnlyProductDetails,
    readOnly,
    selectedReplicatedFromProductID,
    location.state?.addNewProductConfig
  ]);

  useEffect(() => {
    let callFetchFGStatus = false;

    if (selectedReplicatedFromProductID) {
      callFetchFGStatus = false;
    } else if (location.state?.addNewProductConfig && !isInitialSaveSuccess) {
      callFetchFGStatus = true;
    } else {
      callFetchFGStatus = false;
    }

    if(callFetchFGStatus) {
      fetchFGStatus();
    }
  }, []);

  // Fallback: after 1s, if still loading and no data, set loading to false
  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        if (isLoading) setIsLoading(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  // Set initial loading state
  useEffect(() => {
    if (formData?.fgStatuses) {
      setIsLoading(false);
    }
  }, [formData?.fgStatuses]);


  const fetchFGStatus = async () => {
    if (!isMounted()) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(
        `${apiBaseUrl}/fetchFGStatusesDirect/true`
      );
      if (!isMounted()) return;
      if (response?.data?.data) {
        const apiData = response.data.data;
        const isAddFlow = !isStatusEditMode && !viewOnlyProductDetails;

        // Process the data based on whether we're in add flow or edit mode
        const processedData = {
          poStatuses: Array.isArray(apiData.poStatuses)
            ? apiData.poStatuses.map((status) => {
              return {
                status_id: status.status_id,
                status_name: status.status_name,
                value: isAddFlow ? status.activated : false
              };
            })
            : [],
        };


        // If we're in edit mode and not empty, toggle only the activated ones
        if (((editPendingStatus) || isStatusEditMode) && !emptyFgStatuses) {
          const activatedStatuses = formData?.fgStatuses || updateModeData?.productFGStatuses?.fgStatusesJSON || {};
          // Update PO Statuses
          if (Array.isArray(activatedStatuses.poStatuses)) {
            processedData.poStatuses = processedData.poStatuses.map(status => {
              const isActivated = activatedStatuses.poStatuses.some(
                activated => activated.status_id === status.status_id && activated.activated === true
              );
              return {
                ...status,
                value: isActivated
              };
            });
          }
        }

        setStepData(processedData);
        if (typeof updateStepData === "function") {
          updateStepData("fgStatuses", processedData);
        }
      } else {
        setError("Invalid data structure received from API");
      }
    } catch (error) {
      if (isMounted()) {
        console.error(error, "error-saveFGStatuses")
        setError("An error occurred while saving. Please try again later.");
      }
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  };

  // Handle toggle changes for status items
  const handleToggleStatus = (category, statusId, checked) => {
    if (!isMounted() || readOnly) return;
    setStepData((prevData) => {
      const updatedData = JSON.parse(JSON.stringify(prevData));
      if (category === "postatus" && updatedData.poStatuses) {
        const statusIndex = updatedData.poStatuses.findIndex(
          (item) => item && item.status_id === statusId
        );
        if (statusIndex !== -1) {
          updatedData.poStatuses[statusIndex].value = checked;
        }
      }

      if (typeof updateStepData === "function") {
        updateStepData("fgStatuses", updatedData);
      }
      return updatedData;
    });
  };


  /**
   * Saves finished goods statuses to the API
   *
   * This function formats and sends ALL statuses (regardless of toggle state)
   * to the backend API with their activated state. Each status has an 'activated'
   * property that indicates whether it's toggled on or off. This ensures the API
   * has a complete record of all available statuses and their current state.
   *
   * @returns {Promise<boolean>} Promise resolving to true if save was successful, false otherwise
   */
  const saveFGStatuses = async () => {
    try {
      if (isMounted()) {
        setIsSaving(true);
        setError(null);
      }


      // Format data to include ONLY selected statuses with activated property
      const formattedData = {
        poStatuses: Array.isArray(stepData.poStatuses)
          ? stepData.poStatuses
            .filter((status) => {
              return status && status.value === true;
            })
            .map(({ status_id, status_name, value }) => ({
              status_id,
              status_name,
              activated: true
            }))
          : [],
      };

      // Validate that at least one status is selected
      const hasSelectedStatus = formattedData.poStatuses.length > 0;

      if (!hasSelectedStatus) {
        setSnackbar({
          open: true,
          message: "Please select at least one status before proceeding",
          severity: "error",
          key: Date.now(),
        });
        setIsSaving(false);
        return false;
      }

      // Prepare payload
      const payload = {
        vendorMosymphonyID: updateModeData?.productDetails?.vendorMosymphonyID || formData.productDetails?.vendorMosymphonyID,
        collabrixProductID: updateModeData?.productDetails?.collabrixProductID || formData.productDetails?.collabrixProductID,
        productFGStatuses: {
          fgStatusesJSON: formattedData,
        },
      };
      // Call API to save the data
      let response;

      if (isInitialSaveSuccess || (editPendingStatus && !emptyFgStatuses)) {
        // Build update-specific payload
        const updatePayload = {
          ...payload,
          updateFGStatusesFlag: true,
          productDetails: updateModeData?.productDetails,
        };

        delete updatePayload.vendorMosymphonyID;
        delete updatePayload.collabrixProductID;      // Compare current selections with existing data
        const existingStatuses = updateModeData?.productFGStatuses?.fgStatusesJSON || {};
        const hasChanges = JSON.stringify(formattedData.poStatuses) !== JSON.stringify(existingStatuses.poStatuses || []);
        
        if (!hasChanges) {
          // If no changes, just proceed to next step without API call
          
          handleNext();
          return true;
        }
        
        response = await axios.post(
          `${apiBaseUrl}/updateProductDetails`,
          updatePayload
        );
        
        // Mark API call as successful
        if (typeof markApiCallSuccessful === 'function' && response.data && response.data.status) {
          markApiCallSuccessful(); // This handles both showing success message and navigation
          return true;
        }} else {
        // Initial save API endpoint
        response = await axios.post(
          `${apiBaseUrl}/addProductFgStatuses`,
          payload
        );
        
        // If initial save is successful
        if (response.data && response.data.status) {
          setIsInitialSaveSuccess(true);
          // Store in sessionStorage
          sessionStorage.setItem(`addedStatusSelection`, "true");
          sessionStorage.removeItem('emptyFgStatuses');
          
          // Mark API call as successful and handle navigation
          if (typeof markApiCallSuccessful === 'function') {
            markApiCallSuccessful();
            return true;
          }
          
          // Fallback if markApiCallSuccessful is not available
          handleNext();
          return true;
        }
      }
      
      // Handle error cases
      if (isMounted()) {
        setSnackbar({
          open: true,
          message: "Failed to save statuses. Please try again.",
          severity: "error",
          key: Date.now(),
        });
      }
      return false;
    } catch (error) {
      if (isMounted()) {
        console.error(error, "error-saveFGStatuses")
        setError("An error occurred while saving. Please try again later.");
      }
      return false;
    } finally {
      if (isMounted()) {
        setIsSaving(false);
      }
    }
  };

  // Helper function to render each status type table
  // Takes a title, array of statuses, and category identifier
  const renderStatusTable = (title, statuses, category) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ fontWeight: "bold" }}>{title}</TableCell>
            <TableCell style={{ fontWeight: "bold" }} align="right">
              Selection
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.isArray(statuses) && statuses?.length > 0 ? (
            statuses.map((status) => {
              const isChecked = status?.value || false;
              const isDisabled = readOnly;
              
              return (
                <TableRow key={status?.status_id || Math.random()}>
                  <TableCell>{status?.status_name || "Unknown"}</TableCell>
                  <TableCell align="right">
                                      <span style={{ position: "relative" }}>
                    <IOSSwitch
                      className="ios-switch"
                      checked={isChecked}
                      onChange={(e) =>
                        handleToggleStatus(
                          category,
                          status?.status_id,
                          e.target.checked
                        )
                      }
                      color="primary"
                      disabled={isDisabled}
                      isAlwaysEnabled={false}
                    />
                    {isChecked && <Check className="checked-switch" />}
                  </span>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow className="no-status-row">
              <TableCell colSpan={2} align="center">
                No statuses are available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Handle closing snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Validation logic
  const validate = () => {
    let isValid = false;
    if (stepData.poStatuses && stepData.poStatuses.some(s => s.value)) {
      isValid = true;
    }
    return isValid;
  };

  // Call validation on every stepData change
  useEffect(() => {
    onValidationChange(validate());
  }, [stepData, onValidationChange]);


  return (
    <form className={classes?.formContainer}>
      {/* Full Page Loader */}
      {(isLoading || isSaving) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" thickness={7} />
        </div>
      )}

      <Typography variant="h6" className={classes?.formTitle}>
        Status Selection
      </Typography>{" "}
      <Typography variant="h5" style={{ marginBottom: "20px" }}>
        PO Status {!readOnly && <span style={{ color: "red" }}>*</span>}
      </Typography>
      {error ? (
        <Box display="flex" justifyContent="center" alignItems="center" my={3}>
          <Typography variant="body2" style={{ color: "red" }}>
            {error}
          </Typography>
        </Box>
      ) : (
        <>
          {readOnly && !formData?.fgStatuses?.poStatuses?.length ? (
            <Typography align="center" style={{ margin: "32px 0" }}>
              No statuses are selected
            </Typography>
          ) : (
            <Grid container spacing={3} className="stepper-table">
              <Grid item xs={12} md={4}>
                <Paper elevation={0}>
                  {renderStatusTable("PO Status", stepData.poStatuses, "postatus")}
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
      {/* Navigation Buttons - Only show in normal add flow */}
      {(!readOnly && !viewOnlyProductDetails && !isStatusEditMode) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: activeStep === 0 ? "center" : "space-between",
          }}
          className="stepper-navigation-btns"
          mt={4}
        >
          {activeStep !== 0 && (
            <>
              <Button
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                className={classes.button}
              >
                Back
              </Button>{" "}
              <Box sx={{ flex: "1 1 auto" }} />
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={isSaving}
            onClick={
              activeStep === stepsLength - 1 ? handleSubmit : saveFGStatuses
            }
            className={classes.button}
          >
            {isSaving ? (
              <>
                <CircularProgress
                  size={20}
                  color="inherit"
                  style={{ marginRight: 8 }}
                />
                Saving...
              </>
            ) : activeStep === stepsLength - 1 ? (
              "Save"
            ) : (
              "Save & Next"
            )}
          </Button>
        </Box>
      )}
      {/* Error Snackbar */}
      <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        style={{ zIndex: 9999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          style={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </form>
  );
};

// Export the component as default
export default StatusSelection;
